import {
  type StatefulPromise,
  transformToStatefulPromise,
} from 'src/internal/revamp/fetchUtils/statefulPromise';
import ObservableData from 'src/internal/revamp/observable/ObservableData';
import shouldGoToNetwork from 'src/internal/revamp/queryUtils/shouldGoToNetwork';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';
import {getOperationName} from 'src/internal/shared/utils/gql';

import type {ApolloLinkExecuteContext} from 'src/internal/revamp/apollo/ApolloLinkExecutor';
import type ApolloLinkExecutor from 'src/internal/revamp/apollo/ApolloLinkExecutor';
import type MemoryCache from 'src/internal/revamp/cache/MemoryCache';
import type {GraphQlOperationErrors} from 'src/internal/revamp/graphql/types/errors';
import type {Subscription} from 'src/internal/revamp/observable/ObservableData';
import type ResultStore from 'src/internal/revamp/observable/ResultStore';
import type {Observer} from 'src/internal/revamp/observable/types';
import type PollingClock from 'src/internal/revamp/polling/PollingClock';
import type {PollConfig} from 'src/internal/revamp/polling/types';
import type {
  DataOfDoc,
  ErrorExtensionsOfDoc,
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/shared/graphql/types/document';
import type {ObservabilityFns} from 'src/internal/shared/observability/types';

export default class ObservableQuery<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> extends ObservableData<TDocumentNode> {
  // Used to avoid requests from multiple queries mounted at the same time
  private promise: StatefulPromise<void> | null = null;

  private currentFetchResolve: (() => void) | null = null;

  private currentApolloSubscription: {unsubscribe: () => void} | null = null;

  private apolloLinkExecutor: ApolloLinkExecutor;
  private latestApolloLinkContext: ApolloLinkExecuteContext | null = null;
  private observabilityFns: ObservabilityFns;

  private memoryCache: MemoryCache;

  constructor(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    resultStore: ResultStore,
    pollingClock: PollingClock | null,
    memoryCache: MemoryCache,
    apolloLinkExecutor: ApolloLinkExecutor,
    observabilityFns: ObservabilityFns,
  ) {
    super(query, variables, resultStore, pollingClock);

    this.memoryCache = memoryCache;
    this.apolloLinkExecutor = apolloLinkExecutor;
    this.observabilityFns = observabilityFns;
  }

  public subscribe(
    observer: Observer<TDocumentNode>,
    apolloLinkContext: ApolloLinkExecuteContext,
    pollConfig?: PollConfig,
  ): Subscription {
    this.observers.add(observer);

    this.latestApolloLinkContext = apolloLinkContext;

    if (pollConfig) {
      this.registerPollConfig(observer, pollConfig);
      // Now that we have a real observer with poll config, we can clean up
      // the suspense observer that was registered in startAsyncWaitOnResult.
      this.unregisterSuspensePollConfig();
    }

    const shouldFetch = shouldGoToNetwork(
      this.query,
      this.variables,
      this.resultStore,
      this.memoryCache,
    );

    if (shouldFetch) {
      // Don't start a new fetch if retries have been exhausted - the error
      // should be thrown to the user, not retried again.
      const retriesExhausted = this.getPollingState()?.isStopped ?? false;

      if (
        !retriesExhausted &&
        (!this.promise || this.promise.status !== 'pending')
      ) {
        this.setIsPending(true);
        this.promise = this.fetch();
      }
    }

    const watchCleanupFn = this.watch();

    return {
      unsubscribe: () => {
        this.observers.delete(observer);
        // Don't unregister poll config if there's a pending fetch with retry configured -
        // we need the retry config to remain active until the fetch completes. This handles
        // the case where React suspends (throws promise) and may call unsubscribe before
        // the error handler has a chance to process the retry.
        const hasRetryConfig = this.getEffectiveRetryConfig() !== null;
        if (!hasRetryConfig || !this.isCurrentlyFetching()) {
          this.unregisterPollConfig(observer);
        }
        watchCleanupFn();

        // If this was the last observer, cleanup any in-flight subscription.
        // But don't cleanup if we're currently fetching AND retry is configured -
        // we need to let the fetch complete for retries to work. The promise and
        // subscription will be cleaned up when the fetch completes or retries are exhausted.
        if (
          this.observers.size === 0 &&
          this.currentApolloSubscription &&
          (!hasRetryConfig || !this.isCurrentlyFetching())
        ) {
          this.currentApolloSubscription.unsubscribe();
          this.currentApolloSubscription = null;
          this.promise = null;
        }
      },
    };
  }

  public refetch(apolloLinkContext?: ApolloLinkExecuteContext): void {
    if (this.getPollingState()?.isRetrying) {
      return;
    }
    if (!this.promise || this.promise.status !== 'pending') {
      this.resetPollTimer();
      this.setIsPending(true);
      if (apolloLinkContext) {
        this.latestApolloLinkContext = apolloLinkContext;
      }
      this.promise = this.fetch();
    }
  }

  private fetch() {
    return transformToStatefulPromise(
      new Promise<void>((resolve) => {
        this.currentFetchResolve = resolve;
        this.doFetch();
      }),
    );
  }

  private doFetch(): void {
    if (!this.latestApolloLinkContext) {
      const operationName = getOperationName(this.query);
      reportRuntimeError(
        this.observabilityFns,
        `ObservableQuery is missing Apollo Link Context for operation ${operationName}`,
      );

      this.completeFetch();
      return;
    }

    let lastErrors: GraphQlOperationErrors<
      ErrorExtensionsOfDoc<TDocumentNode>
    > | null = null;

    this.currentApolloSubscription = this.apolloLinkExecutor.execute(
      this.query,
      this.variables,
      this.latestApolloLinkContext,
      {
        next: (result: {
          data: DataOfDoc<TDocumentNode> | null;
          errors: GraphQlOperationErrors<
            ErrorExtensionsOfDoc<TDocumentNode>
          > | null;
        }) => {
          lastErrors = result.errors;
          this.handleFetchResult(result);
        },

        complete: () => {
          this.currentApolloSubscription = null;
          if (!lastErrors) {
            this.handleFetchResultForPolling(null);
            this.completeFetch();
          }
        },
      },
    );
  }

  private handleFetchResult(result: {
    data: DataOfDoc<TDocumentNode> | null;
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null;
  }): void {
    const hasExistingData = this.latestResult.data !== null;

    this.handleFetchResultForPolling(result.errors);
    const isRetrying = this.getPollingState()?.isRetrying ?? false;

    if (isRetrying) {
      return;
    }

    // When retry is configured and we have existing data, suppress unexpected
    // errors while retries are still possible. Once retries are exhausted
    // (isStopped), errors must be stored so useQuery can throw to ErrorBoundary.
    const hasRetryConfig = this.getEffectiveRetryConfig() !== null;
    const retriesExhausted = this.getPollingState()?.isStopped ?? false;
    if (
      hasRetryConfig &&
      hasExistingData &&
      result.errors &&
      this.hasUnexpectedErrors(result.errors) &&
      !retriesExhausted
    ) {
      this.setIsPending(false);
      this.completeFetch();
      return;
    }

    this.setIsPending(false);
    this.resultStore.setFetchingResults(this.query, this.variables, {
      data: result.data,
      errors: result.errors,
    });

    if (result.errors) {
      this.completeFetch();
    }
  }

  private completeFetch(): void {
    if (this.currentFetchResolve) {
      this.currentFetchResolve();
      this.currentFetchResolve = null;
    }
  }

  protected retryFetch(): void {
    this.doFetch();
  }

  public startAsyncWaitOnResult(
    apolloLinkContext: ApolloLinkExecuteContext,
    pollConfig?: PollConfig,
  ): StatefulPromise<void> {
    if (this.promise && this.promise.status !== 'fulfilled') {
      return this.promise;
    }

    this.latestApolloLinkContext = apolloLinkContext;

    // Register poll config for suspense phase. This ensures retry config
    // is available when the fetch completes, even before subscribe is called.
    // We use a special "suspense" key that will be cleaned up when real
    // observers subscribe.
    if (pollConfig) {
      this.registerSuspensePollConfig(pollConfig);
    }

    if (
      !shouldGoToNetwork(
        this.query,
        this.variables,
        this.resultStore,
        this.memoryCache,
      )
    ) {
      const alreadyResolved = transformToStatefulPromise(Promise.resolve());
      // Force the 'fulfilled' status based on latest official recommendations
      // https://github.com/reactjs/react.dev/pull/8125
      if (alreadyResolved.status === 'pending') {
        Object.assign(alreadyResolved, {
          status: 'fulfilled' as const,
          value: undefined,
        });
      }
      this.promise = alreadyResolved;
      return this.promise;
    }

    this.setIsPending(true);
    this.promise = this.fetch();

    return this.promise;
  }

  protected isCurrentlyFetching(): boolean {
    return this.promise?.status === 'pending';
  }

  protected triggerRefetch(): void {
    this.refetch();
  }
}
